/**
 * IntakeFormDataContext
 * --------------------
 * Single source of truth for all intake form_data fields.
 * Shared by the header, Collect screen, Review screen, and right panel.
 *
 * - Owns the RTK Query subscription for the intake
 * - Holds server data + a local dirty patch
 * - Provides a merged view so all readers see the latest values
 * - `setField(path, value)` updates the dirty patch (no API call)
 * - Auto-saves dirty changes after a 2s debounce
 * - `save()` flushes immediately
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  mergeFormDataPatchWithRepairs,
  sanitizeIntakeFormData,
} from '@/components/intakes/utils/formDataSanitizers';
import {
  useGetIntakeQuery,
  useUpdateIntakeMutation,
} from '@/services/api/intakeService';
import type { Intake } from '@/services/types/intake-types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = Record<string, any>;

interface IntakeFormDataContextValue {
  /** Merged form_data (server + local edits) */
  formData: FormData;
  /** Raw server form_data (before dirty merge) */
  serverFormData: FormData;
  /** Full intake object (for status, id, firm, etc.) */
  intake: Intake | undefined;
  /** Whether there are unsaved local edits */
  isDirty: boolean;
  /** Whether data is loading from server */
  isLoading: boolean;
  /** Set a field at a dot-notation path (e.g., "personal_info.email") */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setField: (path: string, value: any) => void;
  /** Flush all dirty changes immediately */
  save: () => Promise<void>;
  /** Refetch intake data from server */
  refetch: () => void;
}

const IntakeFormDataContext = createContext<IntakeFormDataContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Immutable set at a dot-notation path (supports array indices like "defendants.0.name") */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setNestedImmutable(obj: FormData, path: string, value: any): FormData {
  const parts = path.split('.');
  if (parts.length === 1) {
    // If the key is a numeric index and obj is an array, clone the array
    if (Array.isArray(obj)) {
      const idx = parseInt(parts[0], 10);
      if (!Number.isNaN(idx)) {
        const arr = [...obj];
        arr[idx] = value;
        return arr as unknown as FormData;
      }
    }
    return { ...obj, [parts[0]]: value };
  }
  const [head, ...rest] = parts;
  const nextKey = rest[0];
  const isNextNumeric = /^\d+$/.test(nextKey);
  // Determine the default child: array if next key is numeric, object otherwise
  const existingChild = Array.isArray(obj) ? obj[parseInt(head, 10)] : obj[head];
  const child = existingChild ?? (isNextNumeric ? [] : {});
  const updatedChild = setNestedImmutable(child as FormData, rest.join('.'), value);

  if (Array.isArray(obj)) {
    const idx = parseInt(head, 10);
    if (!Number.isNaN(idx)) {
      const arr = [...obj];
      arr[idx] = updatedChild;
      return arr as unknown as FormData;
    }
  }
  return { ...obj, [head]: updatedChild };
}

/** Deep merge source into target (source wins).
 *  Arrays of objects are merged element-by-element (e.g., defendants, treatments).
 *  Primitive arrays are replaced entirely. */
function deepMerge(target: FormData, source: FormData): FormData {
  const result = { ...target };
  Object.keys(source).forEach((key) => {
    const s = source[key];
    const t = result[key];
    if (
      s && typeof s === 'object' && !Array.isArray(s) &&
      t && typeof t === 'object' && !Array.isArray(t)
    ) {
      // Both are plain objects → recurse
      result[key] = deepMerge(t as FormData, s as FormData);
    } else if (Array.isArray(s) && Array.isArray(t)) {
      // Both are arrays → merge element-by-element if elements are objects
      const merged = [...t];
      s.forEach((item, idx) => {
        if (item && typeof item === 'object' && !Array.isArray(item) &&
            merged[idx] && typeof merged[idx] === 'object' && !Array.isArray(merged[idx])) {
          // Merge objects at same index
          merged[idx] = deepMerge(merged[idx] as FormData, item as FormData);
        } else if (item !== undefined) {
          // Replace primitive or set new index
          merged[idx] = item;
        }
      });
      result[key] = merged;
    } else if (s !== undefined) {
      result[key] = s;
    }
  });
  return result;
}

// ---------------------------------------------------------------------------
// Auto-save debounce interval
// ---------------------------------------------------------------------------

// Save quickly after user stops typing — responsive but not on every keystroke
const AUTO_SAVE_DEBOUNCE_MS = 500;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface IntakeFormDataProviderProps {
  /** Intake ID to subscribe to */
  intakeId: string;
  children: React.ReactNode;
}

export const IntakeFormDataProvider: React.FC<IntakeFormDataProviderProps> = ({
  intakeId,
  children,
}) => {
  // ---------------------------------------------------------------------------
  // Server data subscription
  // ---------------------------------------------------------------------------

  const {
    data: intake,
    isLoading,
    refetch,
  } = useGetIntakeQuery(intakeId, {
    skip: !intakeId,
    refetchOnMountOrArgChange: true,
  });

  const rawApiFormData = useMemo(
    () => (intake?.form_data ?? {}) as FormData,
    [intake?.form_data],
  );

  // Sanitize form_data from API response
  const apiFormData: FormData = useMemo(() => {
    return sanitizeIntakeFormData(rawApiFormData);
  }, [rawApiFormData]);

  const [updateIntake] = useUpdateIntakeMutation();

  // ---------------------------------------------------------------------------
  // Local server data mirror
  // Starts from API data, and after each save we merge the saved patch into it
  // so the local view stays correct without needing to refetch.
  // ---------------------------------------------------------------------------

  const [localServerData, setLocalServerData] = useState<FormData>(apiFormData);

  // Sync from API when it changes (initial load, external refetch, page mount)
  useEffect(() => {
    setLocalServerData(apiFormData);
  }, [apiFormData]);

  // This is what all consumers see as "server data"
  const serverFormData = localServerData;

  // ---------------------------------------------------------------------------
  // Dirty patch management
  // ---------------------------------------------------------------------------

  const [dirtyPatch, setDirtyPatch] = useState<FormData>({});
  const dirtyPatchRef = useRef<FormData>({});
  const rawApiFormDataRef = useRef<FormData>(rawApiFormData);

  useEffect(() => {
    rawApiFormDataRef.current = rawApiFormData;
  }, [rawApiFormData]);

  const isDirty = Object.keys(dirtyPatch).length > 0;

  // Merged view: local server data + dirty patch (dirty wins)
  const formData = useMemo(
    () => deepMerge(serverFormData, dirtyPatch),
    [serverFormData, dirtyPatch],
  );

  // ---------------------------------------------------------------------------
  // setField — synchronous dirty patch update (no API call)
  // ---------------------------------------------------------------------------

  const setField = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (path: string, value: any) => {
      setDirtyPatch((prev) => {
        const next = setNestedImmutable(prev, path, value);
        dirtyPatchRef.current = next;
        return next;
      });
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // save — flush dirty patch to server immediately
  // ---------------------------------------------------------------------------

  const saveRef = useRef<() => Promise<void>>();

  // Track whether a save is in flight — prevents double-saves
  const isSavingRef = useRef(false);
  // Snapshot of the patch that was sent — used to detect new changes during save
  const savedSnapshotRef = useRef<string>('');

  // Resolve when the current in-flight save completes
  const saveCompleteCallbacksRef = useRef<(() => void)[]>([]);

  const save = useCallback(async () => {
    const patch = dirtyPatchRef.current;
    if (Object.keys(patch).length === 0) return;
    // If a save is already in flight, wait for it then retry
    if (isSavingRef.current) {
      await new Promise<void>((resolve) => {
        saveCompleteCallbacksRef.current.push(resolve);
      });
      // After the in-flight save completed, re-check if still dirty
      if (Object.keys(dirtyPatchRef.current).length === 0) return;
      if (isSavingRef.current) return; // another save started, let it handle it
    }

    try {
      isSavingRef.current = true;
      // Send just the dirty patch — the backend now handles element-by-element
      // array merging (defendants, treatments, etc.), so sparse patches like
      // { defendants: [{ policy_number: 'x' }] } are merged correctly with
      // existing array items on the server. No frontend section-merge needed.
      const sanitized = mergeFormDataPatchWithRepairs(rawApiFormData, {
        ...patch,
      });
      // Snapshot what we're sending so we can detect new changes during save
      savedSnapshotRef.current = JSON.stringify(patch);
      await updateIntake({
        id: intakeId,
        skipOptimistic: true,
        form_data: sanitized,
      }).unwrap();

      // After successful save: merge the saved patch into localServerData
      // so the view stays correct without refetching (which causes cascades).
      // We know exactly what the server has — it's serverFormData + the patch we just sent.
      const currentPatch = JSON.stringify(dirtyPatchRef.current);
      if (currentPatch === savedSnapshotRef.current) {
        // No new changes since save started — merge saved patch into local mirror and clear dirty
        setLocalServerData((prev) => deepMerge(prev, sanitized));
        setDirtyPatch({});
        dirtyPatchRef.current = {};
      } else {
        // New changes arrived during save — merge the SAVED portion into server mirror,
        // but keep the new dirty changes
        setLocalServerData((prev) => deepMerge(prev, sanitized));
        // The dirty patch still has the new changes — auto-save will pick them up
      }
      isSavingRef.current = false;
      savedSnapshotRef.current = '';
      saveCompleteCallbacksRef.current.forEach((cb) => cb());
      saveCompleteCallbacksRef.current = [];
    } catch (error) {
      isSavingRef.current = false;
      savedSnapshotRef.current = '';
      saveCompleteCallbacksRef.current.forEach((cb) => cb());
      saveCompleteCallbacksRef.current = [];
      console.error('Failed to save form data:', error);
    }
  }, [intakeId, rawApiFormData, updateIntake]);

  saveRef.current = save;

  // No refetch-based dirty clearing needed.
  // Dirty patch is cleared directly in save() after successful PATCH.
  // This avoids the cascade: refetch → shared RTK cache update →
  // IntakeFormPage recomputes V2 data → overwrites local state → wipes inputs.

  // ---------------------------------------------------------------------------
  // Auto-save — debounced, fires when dirty patch changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (Object.keys(dirtyPatch).length === 0) return undefined;

    const timer = setTimeout(() => {
      saveRef.current?.();
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [dirtyPatch]);

  // Save immediately on unmount to prevent data loss
  useEffect(() => {
    return () => {
      const patch = dirtyPatchRef.current;
      if (Object.keys(patch).length > 0) {
        // Fire and forget — we're unmounting so can't await
        updateIntake({
          id: intakeId,
          skipOptimistic: true,
          form_data: mergeFormDataPatchWithRepairs(
            rawApiFormDataRef.current,
            { ...patch },
          ),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeId]); // Only depend on intakeId — use refs for the rest

  // ---------------------------------------------------------------------------
  // Reset dirty patch when intake ID changes
  // ---------------------------------------------------------------------------

  const prevIntakeIdRef = useRef(intakeId);
  useEffect(() => {
    if (prevIntakeIdRef.current !== intakeId) {
      setDirtyPatch({});
      dirtyPatchRef.current = {};
      prevIntakeIdRef.current = intakeId;
    }
  }, [intakeId]);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const value = useMemo(
    () => ({
      formData,
      serverFormData,
      intake,
      isDirty,
      isLoading,
      setField,
      save,
      refetch,
    }),
    [formData, serverFormData, intake, isDirty, isLoading, setField, save, refetch],
  );

  return (
    <IntakeFormDataContext.Provider value={value}>
      {children}
    </IntakeFormDataContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useIntakeFormData = (): IntakeFormDataContextValue | null => {
  return useContext(IntakeFormDataContext);
};
